//Mar25 TextEditorToolbar.jsx:
// Enterprise editor toolbar stabilization pass:
// - Preserve existing toolbar UI and public behavior.
// - Add optional onToolbarMouseDown so RichTextEditorBase/TextEditor can save
//   the current contentEditable selection before MUI controls steal focus.
// - Prevent formatting IconButtons from moving focus away from contentEditable.
// - Add optional disabled and sx props for reusable editor contexts.
// - Backward-compatible with existing TextEditor usage.

import React, { useState } from 'react';
import { Box, IconButton, Tooltip, Select, MenuItem, Popover } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FontListComponent from './FontListComponent'; // Importing FontListComponent

const fontSizeOptions = ['12px', '16px', '24px', '32px', '48px', '64px', '96px'];

const alignmentOptions = [
  { value: 'left', icon: <FormatAlignLeftIcon sx={{ fontSize: '18px' }} /> },
  { value: 'center', icon: <FormatAlignCenterIcon sx={{ fontSize: '18px' }} /> },
  { value: 'right', icon: <FormatAlignRightIcon sx={{ fontSize: '18px' }} /> },
  { value: 'justify', icon: <FormatAlignJustifyIcon sx={{ fontSize: '18px' }} /> },
];

const TextEditorToolbar = ({
  activeStyles,
  onStyleChange,
  onToolbarMouseDown,
  disabled = false,
  sx,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const preserveEditorSelection = () => {
    if (onToolbarMouseDown) {
      onToolbarMouseDown();
    }
  };

  const handleToolbarButtonMouseDown = (event) => {
    // Prevent toolbar buttons from stealing focus from contentEditable.
    event.preventDefault();
    preserveEditorSelection();
  };

  const handleOpenPopover = (event) => {
    preserveEditorSelection();
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    preserveEditorSelection();
    setAnchorEl(null);
  };

  const isPopoverOpen = Boolean(anchorEl);

  return (
    <>
      {/* Main Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          p: 0.5,
          bgcolor: '#f1f3f4',
          borderRadius: 1,
          boxShadow: 1,
          flexWrap: 'wrap',
          '& > *': { flexShrink: 0 },
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
          ...sx,
        }}
      >
        {/* Font Family Selector */}
        <Box onMouseDown={preserveEditorSelection}>
          <FontListComponent
            activeFont={activeStyles.fontFamily}
            onChange={(font) => {
              preserveEditorSelection();
              onStyleChange('fontFamily', font);
            }}
          />
        </Box>

        {/* Font Size Selector */}
        <Select
          value={activeStyles.fontSize}
          onMouseDown={preserveEditorSelection}
          onOpen={preserveEditorSelection}
          onChange={(e) => {
            preserveEditorSelection();
            onStyleChange('fontSize', e.target.value);
          }}
          displayEmpty
          size="small"
          disabled={disabled}
          sx={{
            minWidth: 75,
            maxWidth: 75,
            fontSize: '0.75rem',
            border: 'none',
            boxShadow: 'none',
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
          }}
        >
          {fontSizeOptions.map((size) => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </Select>

        {/* Three Dots Menu */}
        <Tooltip title="More Options" arrow>
          <IconButton
            onMouseDown={handleToolbarButtonMouseDown}
            onClick={handleOpenPopover}
            disabled={disabled}
            sx={{
              p: 0.5,
              '& svg': {
                fontSize: '20px',
              },
            }}
          >
            <MoreVertIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Expanded Toolbar as Popover */}
      <Popover
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          mt: 0.5,
          '.MuiPaper-root': {
            borderRadius: '12px', // Add rounded corners to the popover itself
            maxHeight: '50px', // Reduce height of the popover
          },
        }}
      >
        <Box
          onMouseDown={preserveEditorSelection}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.7,
            pl: 1,
            bgcolor: '#f1f3f4',
            borderRadius: 1,
            boxShadow: 1,
          }}
        >
          {/* Bold Button */}
          <Tooltip title="Bold" arrow>
            <IconButton
              onMouseDown={handleToolbarButtonMouseDown}
              onClick={() => {
                preserveEditorSelection();
                onStyleChange('bold', !activeStyles.bold);
              }}
              disabled={disabled}
              color={activeStyles.bold ? 'primary' : 'default'}
              sx={{
                p: 0.5,
                '& svg': {
                  fontSize: '20px',
                },
              }}
            >
              <FormatBoldIcon />
            </IconButton>
          </Tooltip>

          {/* Italic Button */}
          <Tooltip title="Italic" arrow>
            <IconButton
              onMouseDown={handleToolbarButtonMouseDown}
              onClick={() => {
                preserveEditorSelection();
                onStyleChange('italic', !activeStyles.italic);
              }}
              disabled={disabled}
              color={activeStyles.italic ? 'primary' : 'default'}
              sx={{
                p: 0.5,
                '& svg': {
                  fontSize: '20px',
                },
              }}
            >
              <FormatItalicIcon />
            </IconButton>
          </Tooltip>

          {/* Underline Button */}
          <Tooltip title="Underline" arrow>
            <IconButton
              onMouseDown={handleToolbarButtonMouseDown}
              onClick={() => {
                preserveEditorSelection();
                onStyleChange('underline', !activeStyles.underline);
              }}
              disabled={disabled}
              color={activeStyles.underline ? 'primary' : 'default'}
              sx={{
                p: 0.5,
                '& svg': {
                  fontSize: '20px',
                },
              }}
            >
              <FormatUnderlinedIcon />
            </IconButton>
          </Tooltip>

          {/* Font Color Selector */}
          <input
            type="color"
            value={activeStyles.fontColor || '#000000'}
            disabled={disabled}
            onMouseDown={preserveEditorSelection}
            onFocus={preserveEditorSelection}
            onChange={(e) => {
              preserveEditorSelection();
              onStyleChange('fontColor', e.target.value);
            }}
            style={{
              width: '24px',
              height: '28px',
              border: 'none',
              padding: '2px',
              borderRadius: '50%',
              cursor: disabled ? 'default' : 'pointer',
            }}
          />

          {/* Text Alignment Selector */}
          <Select
            value={activeStyles.textAlign}
            onMouseDown={preserveEditorSelection}
            onOpen={preserveEditorSelection}
            onChange={(e) => {
              preserveEditorSelection();
              onStyleChange('textAlign', e.target.value);
            }}
            displayEmpty
            size="small"
            disabled={disabled}
            sx={{
              minWidth: 50,
              fontSize: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              border: 'none',
              boxShadow: 'none',
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
            }}
            renderValue={(selected) => {
              const selectedOption = alignmentOptions.find((option) => option.value === selected);
              return selectedOption ? selectedOption.icon : alignmentOptions[0].icon;
            }}
          >
            {alignmentOptions.map((option) => (
              <MenuItem key={option.value} value={option.value} sx={{ display: 'flex', justifyContent: 'center' }}>
                {option.icon}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Popover>
    </>
  );
};

export default React.memo(TextEditorToolbar);